/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/CustomTable'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import Loading from '@/components/Loading'
import { formatCFA, cn } from '@/lib/utils'
import { useBillQuery, useVoidBillMutation } from '@/hooks/useBills'
import {
  useCreatePaymentMutation,
  useDeletePaymentMutation,
  usePaymentsByBillQuery,
} from '@/hooks/usePayments'
import { useCustomerBalanceQuery } from '@/hooks/useCustomers'
import { useReactToPrint } from 'react-to-print'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import BackBtn from '@/components/BackButton'
import type { Bill } from '@/types/backend'
import { Trash2 } from 'lucide-react'
import { BillPrintDocument } from './BillPrintDocument'
import type { OwnerInvoiceProfile, PrintFormat } from '@/lib/invoice-branding'
import {
  useOwnerSettingsQuery,
  useUpdateOwnerSettingsMutation,
} from '@/hooks/useOwnerSettings'

export default function BillDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: bill, status } = useBillQuery(id, !!id)
  const { data: byBill } = usePaymentsByBillQuery(id, !!id)
  const createPayment = useCreatePaymentMutation()
  const deletePayment = useDeletePaymentMutation()
  const voidBill = useVoidBillMutation(id!)

  const { data: ownerSettings } = useOwnerSettingsQuery(true)
  const updateOwnerSettings = useUpdateOwnerSettingsMutation()

  const [payOpen, setPayOpen] = useState(false)
  const [given, setGiven] = useState<string>('')
  const [selectedPrintFormat, setSelectedPrintFormat] =
    useState<PrintFormat>('A4')

  const customerId = bill?.customer?.id
  const { data: customerBalanceData } = useCustomerBalanceQuery(
    customerId,
    !!customerId
  )

  const customerBalance = customerBalanceData?.balance ?? 0
  const customerBalanceTone =
    customerBalance > 0
      ? 'text-red-600'
      : customerBalance < 0
      ? 'text-emerald-600'
      : 'text-muted-foreground'

  const STATUS_FR: Record<Bill['status'], string> = {
    DRAFT: 'Brouillon',
    ISSUED: 'Émise',
    PARTIALLY_PAID: 'Partiellement payée',
    PAID: 'Payée',
    VOID: 'Annulée',
  }

  const billStatusFr = (s: Bill['status']) => STATUS_FR[s] ?? s

  useEffect(() => {
    if (!ownerSettings?.invoice?.printFormat) return
    setSelectedPrintFormat(ownerSettings.invoice.printFormat as PrintFormat)
  }, [ownerSettings?.invoice?.printFormat])

  const printFormat = selectedPrintFormat
  const savedPrintFormat =
    (ownerSettings?.invoice?.printFormat as PrintFormat | undefined) ?? 'A4'
  const isPrintFormatDirty = printFormat !== savedPrintFormat
  const isThermal = printFormat === 'THERMAL_58' || printFormat === 'THERMAL_80'

  const ownerProfile = useMemo<OwnerInvoiceProfile>(() => {
    return {
      hasActivePlan: true, // branchera plus tard la vraie règle plan/trial
      businessName: ownerSettings?.invoice?.businessName || '',
      businessPhone: ownerSettings?.invoice?.businessPhone || '',
      businessAddress: ownerSettings?.invoice?.businessAddress || '',
      invoiceLogoUrl: ownerSettings?.invoice?.logoUrl || '',
      preferredPrintFormat: printFormat,
    }
  }, [
    ownerSettings?.invoice?.businessName,
    ownerSettings?.invoice?.businessPhone,
    ownerSettings?.invoice?.businessAddress,
    ownerSettings?.invoice?.logoUrl,
    printFormat,
  ])

  const handleSavePrintFormat = async () => {
    await updateOwnerSettings.mutateAsync({
      invoice: {
        printFormat,
      },
    })
  }

  const printRef = useRef<HTMLDivElement>(null)
  const PRINT_ID = 'printable-area'

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: bill ? `facture-${bill.id}` : 'facture',
  })

  const handleDownloadPdf = async () => {
    if (!printRef.current || !bill) return

    if (isThermal) {
      handlePrint()
      return
    }

    const canvas = await html2canvas(printRef.current, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      onclone: (doc) => {
        doc.documentElement.classList.remove('dark')

        const style = doc.createElement('style')
        style.textContent = `
          html, body {
            background: #ffffff !important;
            color: #111111 !important;
          }

          *, *::before, *::after {
            color: #111111 !important;
            border-color: #e5e7eb !important;
            box-shadow: none !important;
            filter: none !important;
            background-image: none !important;
          }

          #${PRINT_ID} {
            background: #ffffff !important;
            color: #111111 !important;
          }

          #${PRINT_ID} table thead {
            background: #f8fafc !important;
          }

          :root, #${PRINT_ID}, #${PRINT_ID} * {
            --background: initial !important;
            --foreground: initial !important;
            --muted: initial !important;
            --card: initial !important;
            --popover: initial !important;
            --border: initial !important;
            --input: initial !important;
            --ring: initial !important;
          }
        `
        doc.head.appendChild(style)
      },
    })

    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const imgWidth = pageWidth
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    let heightLeft = imgHeight
    let position = 0

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight

    while (heightLeft > 0) {
      position = heightLeft - imgHeight
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
    }

    pdf.save(`facture-${bill.id}.pdf`)
  }

  useEffect(() => {
    if (!bill) return

    const a = searchParams.get('action')

    if (a === 'print') {
      requestAnimationFrame(() => {
        if (printRef.current) handlePrint()
      })
    } else if (a === 'pdf') {
      requestAnimationFrame(() => {
        if (printRef.current) handleDownloadPdf()
      })
    } else if (a === 'pay') {
      setPayOpen(true)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('action')
          return next
        },
        { replace: true }
      )
    }
  }, [bill, searchParams, handlePrint, handleDownloadPdf, setSearchParams])

  const paymentsColumns = useMemo(
    () => [
      {
        accessorKey: 'incurredOn',
        header: 'Date',
        cell: ({ row }: any) =>
          new Date(row.original.incurredOn).toLocaleString('fr-FR', {
            dateStyle: 'short',
            timeStyle: 'short',
          }),
      },
      { accessorKey: 'method', header: 'Méthode' },
      {
        accessorKey: 'amount',
        header: () => <div className='text-right'>Montant</div>,
        cell: ({ row }: any) => (
          <div
            className={cn(
              'text-right tabular-nums',
              row.original.amount >= 0 ? 'text-emerald-600' : 'text-red-600'
            )}
          >
            {formatCFA(row.original.amount)}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: any) => {
          return (
            <div className='flex justify-end'>
              <Button
                variant='ghost'
                size='sm'
                onClick={() =>
                  deletePayment.mutate({
                    id: row.original.id,
                    billId: row.original.billId,
                    customerId: row.original.customerId,
                  })
                }
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            </div>
          )
        },
      },
    ],
    [deletePayment]
  )

  if (status === 'pending') return <Loading />
  if (!bill) return <div className='p-4'>Facture introuvable.</div>

  const balance = bill.balance ?? 0
  const tone =
    balance > 0
      ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300'
      : balance < 0
      ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300'
      : 'border-muted bg-muted/30 text-foreground/80'

  return (
    <div className='space-y-6'>
      <div className='flex flex-col sm:flex-row gap-y-4 justify-between'>
        <BackBtn />

        <div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
          <Button variant='outline' onClick={handlePrint}>
            Imprimer
          </Button>

          {isThermal ? (
            <Button variant='outline' onClick={handlePrint}>
              Imprimer ticket
            </Button>
          ) : (
            <Button variant='outline' onClick={handleDownloadPdf}>
              Télécharger PDF
            </Button>
          )}

          {bill.status !== 'VOID' && (
            <Button
              variant='destructive'
              onClick={() =>
                voidBill.mutate({ reason: 'Annulation opérateur' })
              }
            >
              Annuler (VOID)
            </Button>
          )}

          <Button
            onClick={() => setPayOpen(true)}
            disabled={bill.status === 'VOID' || bill.status === 'PAID'}
          >
            Ajouter un paiement
          </Button>
        </div>
      </div>

      <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
        <div className='flex flex-col gap-1'>
          <h2 className='text-2xl font-semibold'>
            Facture #{bill.id.slice(0, 8)}
          </h2>
          <div className='text-sm text-muted-foreground'>
            Format actuel :{' '}
            <span className='font-medium text-foreground'>{printFormat}</span>
          </div>
        </div>

        <div className='no-print flex flex-col gap-3 sm:flex-row sm:items-end'>
          <div className='w-full sm:w-[240px]'>
            <Label htmlFor='print-format' className='mb-2 block'>
              Format d’impression
            </Label>

            <select
              id='print-format'
              value={selectedPrintFormat}
              onChange={(e) =>
                setSelectedPrintFormat(e.target.value as PrintFormat)
              }
              className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            >
              <option value='A4'>A4</option>
              <option value='THERMAL_80'>Thermique 80 mm</option>
              <option value='THERMAL_58'>Thermique 58 mm</option>
            </select>
          </div>

          <Button
            type='button'
            onClick={handleSavePrintFormat}
            disabled={!isPrintFormatDirty || updateOwnerSettings.isPending}
            className='sm:min-w-[150px]'
          >
            {updateOwnerSettings.isPending
              ? 'Enregistrement...'
              : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <Card className={cn('max-w-md border', tone)}>
        <CardHeader>
          <CardTitle className='text-lg'>
            {balance > 0
              ? 'À recevoir'
              : balance < 0
              ? 'Avoir client'
              : 'Solde à zéro'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='text-3xl font-bold tabular-nums'>
            {formatCFA(Math.abs(balance))}
          </div>
          <div className='text-xs mt-1 opacity-80'>
            Client : {bill.customer.name} — Statut :{' '}
            <b>{billStatusFr(bill.status)}</b>
          </div>
        </CardContent>
      </Card>

      {typeof customerBalance === 'number' && (
        <div className='no-print rounded-md border p-3 text-sm'>
          <div className='flex items-center justify-between'>
            <span>Solde global client</span>
            <span
              className={cn('tabular-nums font-medium', customerBalanceTone)}
            >
              {customerBalance > 0
                ? `À recevoir ${formatCFA(customerBalance)}`
                : customerBalance < 0
                ? `Avoir ${formatCFA(Math.abs(customerBalance))}`
                : formatCFA(0)}
            </span>
          </div>

          {customerBalance > 0 ? (
            <div className='text-xs mt-1 opacity-80'>
              Ce client vous doit actuellement {formatCFA(customerBalance)} au
              total (toutes factures confondues).
            </div>
          ) : customerBalance < 0 ? (
            <div className='text-xs mt-1 opacity-80'>
              Vous avez un avoir client de{' '}
              {formatCFA(Math.abs(customerBalance))}.
            </div>
          ) : null}
          {bill.status === 'PAID' && customerBalance > 0 && (
            <div className='text-xs mt-2 text-amber-700 dark:text-amber-300'>
              Cette facture est soldée. Pour encaisser un paiement sur la dette
              restante du client, utilise le bouton <b>Encaisser</b> depuis la
              fiche client.
            </div>
          )}
        </div>
      )}

      <BillPrintDocument
        ref={printRef}
        bill={bill}
        ownerProfile={ownerProfile}
        customerBalance={customerBalance}
        printFormat={printFormat}
      />

      <div className='flex items-center justify-between no-print'>
        <h3 className='text-lg font-semibold' id='payments'>
          Paiements
        </h3>
        <Button
          onClick={() => setPayOpen(true)}
          disabled={bill.status === 'VOID' || bill.status === 'PAID'}
        >
          Ajouter un paiement
        </Button>
      </div>

      <div className='no-print'>
        <DataTable<any, unknown>
          columns={paymentsColumns as any}
          data={byBill?.payments ?? []}
        />
      </div>

      <AppDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        title='Ajouter un paiement'
        description='Saisissez le montant donné par le client (given).'
        asForm
        onSubmit={(e) => {
          e.preventDefault()

          const g = Number(given.replace(/\s/g, '').replace(',', '.'))
          if (!isFinite(g) || g <= 0) return

          createPayment.mutate(
            {
              customerId: bill.customer.id,
              billId: bill.id,
              given: g,
              method: 'CASH',
              changePolicy: 'RETURN',
            },
            {
              onSuccess: () => {
                setPayOpen(false)
                setGiven('')
              },
            }
          )
        }}
        footer={
          <ModalActions
            confirmLabel='Valider'
            confirmLoading={createPayment.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <Label htmlFor='given'>Montant donné</Label>
          <Input
            id='given'
            inputMode='decimal'
            value={given}
            onChange={(e) => setGiven(e.target.value)}
            placeholder='Ex: 10000'
            autoFocus
          />
          <div className='text-xs text-muted-foreground'>
            Si “given” &gt; restant : rendra la monnaie (RETURN) ou mettra en
            avoir (CREDIT).
          </div>
        </div>
      </AppDialog>
    </div>
  )
}
